import { z } from 'zod';
import { TENANT_ROLE_KEYS } from './roles.js';
import { RISK_SUBFACTOR_CATALOGS } from './risk-score.js';
import { esPA } from './i18n/es-PA.js';
/** §7 validation rules — shared between API (authoritative) and web (UX). */
export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .email({ message: esPA.users.invalidEmail })
    .max(254, { message: esPA.users.invalidEmail });
export const fullNameSchema = z
    .string()
    .trim()
    .min(2)
    .max(120);
/**
 * Role keys assignable via tenant endpoints. super_admin is rejected with a
 * 400 before RBAC even runs (§7).
 */
export const roleKeysSchema = z
    .array(z.enum(TENANT_ROLE_KEYS))
    .nonempty();
export const planTierSchema = z.enum(['standard', 'premium']).default('standard');
export const userStatusSchema = z.enum(['active', 'deactivated', 'deleted']);
export const tenantStatusSchema = z.enum(['active', 'suspended', 'offboarding']);
export const createUserSchema = z.object({
    fullName: fullNameSchema,
    email: emailSchema,
    roleKeys: roleKeysSchema,
});
export const updateUserSchema = z
    .object({
    fullName: fullNameSchema.optional(),
    status: userStatusSchema.optional(),
})
    .refine((v) => v.fullName !== undefined || v.status !== undefined, {
    message: 'At least one field is required',
});
export const assignRolesSchema = z.object({
    roleKeys: roleKeysSchema,
});
export const listUsersQuerySchema = z.object({
    search: z.string().trim().max(254).optional(),
    status: userStatusSchema.optional(),
});
export const createTenantSchema = z.object({
    legalName: z.string().trim().min(2).max(254),
    planTier: planTierSchema,
    admin: z.object({
        email: emailSchema,
        fullName: fullNameSchema,
    }),
});
export const updateTenantSchema = z
    .object({
    status: tenantStatusSchema.optional(),
    planTier: z.enum(['standard', 'premium']).optional(),
})
    .refine((v) => v.status !== undefined || v.planTier !== undefined, {
    message: 'At least one field is required',
});
// ---------------------------------------------------------------------------
// Expediente (client compliance case) schemas.
// ---------------------------------------------------------------------------
export const expedientePersonaSchema = z.enum(['natural', 'legal']);
export const expedienteEstadoSchema = z.enum([
    'Nuevo',
    'En Revisión',
    'Docs Pendientes',
    'Escalado',
    'Aprobado',
    'Activo',
    'Rechazado',
    'Actualización Requerida',
]);
export const expedienteRiesgoSchema = z.enum(['bajo', 'medio', 'alto', 'sin']);
/** Onboarding channel — a risk-matrix factor input (canal). */
export const expedienteCanalSchema = z.enum([
    'presencial',
    'no_presencial',
    'mixto',
    'corresponsal',
]);
/** Product / service the client holds — a risk-matrix factor input (producto). */
export const expedienteProductoSchema = z.enum([
    'cuenta_basica',
    'ahorro',
    'credito',
    'inversion',
    'efectivo',
    'criptoactivos',
    'comercio_exterior',
]);
export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/**
 * How a subject is identified. `folio` supports new sociedades/fundaciones that
 * have a Registro Público folio but no RUC yet (§1); the rest mirror the id
 * documents already in use. Optional — `doc_id` stays the primary business key.
 */
export const expedienteDocKindSchema = z.enum(['cedula', 'ruc', 'pasaporte', 'folio', 'otro']);
// ---------------------------------------------------------------------------
// KYC registro (Fase 1 — modelo de datos). The registration wizard captures the
// typed client entity kind, the matrix inputs (as catalog keys), and the KYC
// fields the norm demands (Ley 23 Art. 27-28 / Decreto 363). Everything here is
// OPTIONAL so existing callers/rows are unaffected; scoring wires them in Fase 5.
// ---------------------------------------------------------------------------
/** Typed client entity kind — drives the wizard branch and the matrix `estructura`. */
export const expedienteEntityTypeSchema = z.enum([
    'persona_natural',
    'sociedad_anonima',
    'fundacion',
]);
/** Purpose / nature of the relationship (Ley 23 Art. 27 núcleo KYC). */
export const expedientePropositoSchema = z.enum([
    'tenedora_acciones',
    'tenedora_cuenta_bancaria',
    'planeacion_patrimonial',
    'inversiones',
    'actividad_comercial',
    'tenedora_activos',
    'otros',
]);
/** Source of funds (Ley 23 Art. 27 núcleo KYC — origen/fuente de fondos). */
export const expedienteFuenteFondosSchema = z.enum([
    'fondos_negocios',
    'fondos_personales',
    'donacion',
    'prestamo',
    'contribucion_accionistas',
    'herencia',
    'dividendos',
    'otros',
]);
/**
 * Matrix inputs persisted as `expedientes.risk_inputs` (jsonb). Each key holds a
 * CATALOG KEY (not a band) chosen in the wizard; the band derives at scoring time
 * from `RISK_SUBFACTOR_CATALOGS`, so recalibrating a catalog re-scores existing
 * clients (§3 Fase 1 — one column, zero migrations when a catalog changes). The
 * four overrides (sanciones/pep/fatfNegra/antecedente) are the raise-the-floor
 * indicators that sit OUTSIDE the weighted arithmetic. Fase 5 maps this to
 * `RiskLevelInput`; nothing here scores yet.
 */
const RISK_INPUT_CATALOG_KEY = {
    profesion: 'cliente.profesion',
    estructura: 'cliente.estructura',
    antecedentes: 'cliente.antecedentes',
    nacionalidad: 'jurisdiccion.nacionalidad',
    origen_destino: 'jurisdiccion.origen_destino',
    domicilio: 'jurisdiccion.domicilio',
    listas: 'jurisdiccion.listas',
    canal_via: 'canal.canal_via',
    frecuencia: 'canal.frecuencia',
    monto: 'canal.monto',
    instrumento: 'canal.instrumento',
    producto: 'producto',
};
export const riskInputsSchema = z
    .object({
    profesion: z.string().optional(),
    estructura: z.string().optional(),
    antecedentes: z.string().optional(),
    nacionalidad: z.string().optional(),
    origen_destino: z.string().optional(),
    domicilio: z.string().optional(),
    listas: z.string().optional(),
    canal_via: z.string().optional(),
    frecuencia: z.string().optional(),
    monto: z.string().optional(),
    instrumento: z.string().optional(),
    producto: z.string().optional(),
    // Overrides — raise-the-floor indicators, outside the weighted arithmetic.
    sanciones: z.boolean().optional(),
    pep: z.boolean().optional(),
    fatfNegra: z.boolean().optional(),
    antecedente: z.boolean().optional(),
})
    // Validate each chosen key against its catalog at PARSE time (not construction
    // time) so we never depend on module-eval order between schemas ↔ risk-score.
    .superRefine((val, ctx) => {
    for (const [field, catalogKey] of Object.entries(RISK_INPUT_CATALOG_KEY)) {
        const picked = val[field];
        if (picked === undefined)
            continue;
        const ok = RISK_SUBFACTOR_CATALOGS[catalogKey]?.some((e) => e.key === picked) ?? false;
        if (!ok) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [field],
                message: `Clave de catálogo inválida para ${field}`,
            });
        }
    }
});
/** ISO alpha-2 country code (uppercased). Used for país de residencia fiscal. */
const iso2CountrySchema = z.string().trim().length(2).toUpperCase();
export const createExpedienteSchema = z.object({
    persona: expedientePersonaSchema,
    nombre: z.string().trim().min(2).max(254),
    docId: z.string().trim().min(1).max(50),
    sector: z.string().trim().min(1).max(120),
    kind: z.string().trim().min(1).max(120),
    email: emailSchema.optional(),
    phone: z.string().trim().max(30).optional(),
    canal: expedienteCanalSchema.optional(),
    producto: expedienteProductoSchema.optional(),
    // §1 identity-by-folio: how the subject is identified, its Registro Público
    // folio, and its constitution date. All optional and backfill-safe.
    docKind: expedienteDocKindSchema.optional(),
    folio: z.string().trim().max(60).optional(),
    constitutionDate: isoDateSchema.optional(),
    // Captured at intake (OCR prefill from the cédula); required by the Verifik
    // national-registry lookup. Optional and backfill-safe.
    dateOfBirth: isoDateSchema.optional(),
    // Fase 1 KYC registro — all optional (existing wizard callers omit them). The
    // matrix inputs / KYC fields the norm demands; scoring wires them in Fase 5.
    entityType: expedienteEntityTypeSchema.optional(),
    riskInputs: riskInputsSchema.optional(),
    proposito: expedientePropositoSchema.optional(),
    paisesActividad: z.array(z.string().trim().min(1).max(120)).optional(),
    fuenteFondos: expedienteFuenteFondosSchema.optional(),
    // PJ physical address (Ley 23 domicilio real) + PN FATCA/CRS declarations —
    // all optional and backfill-safe (0025).
    direccionFisica: z.string().trim().min(1).max(500).optional(),
    usPerson: z.boolean().optional(),
    paisResidenciaFiscal: iso2CountrySchema.optional(),
    multiplesNacionalidades: z.boolean().optional(),
    // Domicile captured at intake (service-receipt OCR prefill or manual). Optional,
    //  backfill-safe; feeds the address validation workflow via db-case-data fallback.
    direccion: z.string().trim().max(500).optional(),
});
// ---------------------------------------------------------------------------
// OCR prefill — stateless pre-creation extraction from an uploaded id document.
// The client posts the cédula (image/PDF) BEFORE the expediente exists; the API
// runs OCR and returns best-effort fields the intake wizard pre-fills. Nothing
// is persisted, so this reuses `expediente.create` and emits no audit event.
// ---------------------------------------------------------------------------
// ≈4 MB source file, base64-encoded (base64 is ~1.37x). Kept well under
// Lambda's 6 MB hard cap on the whole invocation event, not just this field.
// The web client checks against this same constant before it ever calls the
// API, so an oversized file fails fast client-side with a distinct message.
export const OCR_PREFILL_MAX_FILE_BASE64_CHARS = 5_500_000;
export const ocrPrefillRequestSchema = z.object({
    fileBase64: z.string().min(1).max(OCR_PREFILL_MAX_FILE_BASE64_CHARS),
    contentType: z.string().trim().min(1).max(100),
    kind: z.enum(['id', 'address']).optional(), // absent means 'id'; 'address' = service receipt
});
// ---------------------------------------------------------------------------
// Vigilancia Registral — proxy to the rp-consulta service (services/rp-consulta).
// The RP credentials are ephemeral: validated here, forwarded in the request body
// to the Python service, never stored or logged (I7). Login only carries the
// credentials; the bulk run carries the uploaded folio XLSX as base64.
// ---------------------------------------------------------------------------
export const rpLoginSchema = z.object({
    usuario: z.string().min(1),
    password: z.string().min(1),
    // 2Captcha key + reCAPTCHA sitekey are optional (the service can fall back to
    // its own env). Present only for this request; never persisted.
    twocaptchaApiKey: z.string().min(1).optional(),
    recaptchaSitekey: z.string().min(1).optional(),
});
export const rpLoteSchema = z.object({
    // The uploaded folio XLSX, base64-encoded. Same cap as ocr-prefill — kept well
    // under Lambda's 6 MB whole-invocation event limit.
    fileBase64: z.string().min(1).max(OCR_PREFILL_MAX_FILE_BASE64_CHARS),
    descargarPrelacion: z.boolean().optional(),
    ocr: z.boolean().optional(),
});
/**
 * Fields extracted from the document. All optional because OCR is best-effort:
 * a field is present only when the engine found it, and dates are included only
 * when they normalized cleanly to ISO (YYYY-MM-DD) — never raw OCR text.
 * `direccion`/`fechaEmision` come from `kind: 'address'` (service-receipt) requests;
 * the rest come from the default `kind: 'id'` (cédula) requests.
 */
export const ocrPrefillFieldsSchema = z.object({
    fullName: z.string().optional(),
    idNumber: z.string().optional(),
    birthDate: isoDateSchema.optional(),
    expiryDate: isoDateSchema.optional(),
    // Issuing country the engine detected (ISO 3166-1 alpha-3, e.g. "PAN") — lets
    // the intake wizard pre-select citizenship and format the ID per that country.
    country: z.string().optional(),
    // Service-receipt (recibo de servicio) fields — kind: 'address'.
    direccion: z.string().optional(),
    fechaEmision: isoDateSchema.optional(),
});
/**
 * OCR-prefill outcome. `confidence` is the engine's minimum word confidence
 * (0–100 — min across pages, then min across fields), so one weak page or
 * field biases the whole result; `lowConfidence` tells the UI to nudge the
 * user to review every pre-filled field before continuing (the prefill is a
 * convenience, never authoritative).
 */
export const ocrPrefillResultSchema = z.object({
    fields: ocrPrefillFieldsSchema,
    confidence: z.number().min(0).max(100),
    lowConfidence: z.boolean(),
});
// ---------------------------------------------------------------------------
// Continuous-risk layer (§1) — firm entity mode + bi-temporal risk bitácora.
// ---------------------------------------------------------------------------
/**
 * Firm-level mode that drives which indicator set applies (§2). `financiero` is
 * the default (fullest screening = safe). Only these two values are valid on the
 * tenant-settings endpoint.
 */
// Tipo de sujeto obligado (clase principal por supervisor). Cada uno tiene sus
// propias subcategorías (SectorRiskPreset con este `mode`) y su plantilla. NO se
// agrupan clases distintas: financiero (SBP/IPACOOP), abogados/gatekeepers (SSNF),
// mercado de valores (SMV), seguros (SSRP), inmobiliaria (SNF).
export const entityModeSchema = z.enum([
    'financiero',
    'abogados',
    'valores',
    'seguros',
    'inmobiliaria',
]);
/**
 * What produced a risk-timeline change — the provenance that connects a bitácora
 * row back to a list hit / news / matrix recalc / manual action / onboarding.
 */
export const riskSourceTypeSchema = z.enum([
    'matrix_recalc',
    'list_hit',
    'sanction',
    'pep',
    'antecedente',
    'adverse_media',
    'vigilancia_registral',
    'manual',
    'onboarding',
]);
/**
 * One recorded risk state for an expediente (the §1 bitácora write). The engine
 * closes the prior open row and opens this one in a single transaction (bi-temporal
 * fact invalidation). Optional keys are exactOptionalPropertyTypes-safe: absent
 * means "not provided", and the inferred type never carries an explicit undefined.
 */
export const recordRiskBitacoraSchema = z.object({
    nivel: z.enum(['bajo', 'medio', 'alto', 'sin']),
    motivo: z.string().trim().min(1).max(500),
    sourceType: riskSourceTypeSchema,
    score: z.number().int().min(0).max(100).optional(),
    matrixVersion: z.number().int().min(0).optional(),
    // Free-form provenance payload (list-hit ref, news url, recalc inputs…).
    sourceRef: z.unknown().optional(),
    riskAssessmentId: z.string().uuid().optional(),
    // Scoring factor breakdown captured with this state (engine-shaped, opaque here).
    factors: z.unknown().optional(),
    // Optional UBO/owner-level subject the change pertains to (else expediente-level).
    subjectId: z.string().trim().min(1).max(50).optional(),
});
export const updateEntityModeSchema = z.object({
    entityMode: entityModeSchema,
});
const updateExpedienteFields = z.object({
    nombre: z.string().trim().min(2).max(254).optional(),
    email: emailSchema.nullable().optional(),
    phone: z.string().trim().max(30).nullable().optional(),
    estado: expedienteEstadoSchema.optional(),
    riesgo: expedienteRiesgoSchema.optional(),
    score: z.number().int().min(0).max(100).optional(),
    pep: z.boolean().optional(),
    assignedTo: z.string().uuid().nullable().optional(),
    nextReviewAt: isoDateSchema.nullable().optional(),
    canal: expedienteCanalSchema.nullable().optional(),
    producto: expedienteProductoSchema.nullable().optional(),
    // Full-field edit — the remaining identity/classification fields, reusing the
    // create schema's validators. persona/docId/sector/kind are required at create,
    // so they are non-nullable here (an edit may change them, never clear them);
    // docKind/folio/dateOfBirth/constitutionDate stay backfill-safe (nullable).
    persona: expedientePersonaSchema.optional(),
    docId: z.string().trim().min(1).max(50).optional(),
    sector: z.string().trim().min(1).max(120).optional(),
    kind: z.string().trim().min(1).max(120).optional(),
    docKind: expedienteDocKindSchema.nullable().optional(),
    folio: z.string().trim().max(60).nullable().optional(),
    dateOfBirth: isoDateSchema.nullable().optional(),
    constitutionDate: isoDateSchema.nullable().optional(),
    // Fase 1 KYC registro — nullable so an edit may clear a captured value.
    entityType: expedienteEntityTypeSchema.optional(),
    riskInputs: riskInputsSchema.nullable().optional(),
    proposito: expedientePropositoSchema.nullable().optional(),
    paisesActividad: z.array(z.string().trim().min(1).max(120)).nullable().optional(),
    fuenteFondos: expedienteFuenteFondosSchema.nullable().optional(),
    // PJ physical address + PN FATCA/CRS declarations — nullable so an edit may
    // clear a captured value (0025).
    direccionFisica: z.string().trim().max(500).nullable().optional(),
    usPerson: z.boolean().nullable().optional(),
    paisResidenciaFiscal: iso2CountrySchema.nullable().optional(),
    multiplesNacionalidades: z.boolean().nullable().optional(),
    // Domicile — nullable so an edit may clear a captured value (mirrors email/phone).
    direccion: z.string().trim().max(500).nullable().optional(),
});
export const updateExpedienteSchema = updateExpedienteFields.refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'Al menos un campo es requerido' });
export const listExpedientesQuerySchema = z.object({
    estado: expedienteEstadoSchema.optional(),
    persona: expedientePersonaSchema.optional(),
    // Soft-delete filter: true lists the deleted (papelera) set instead of active.
    // NOTE: z.coerce.boolean() would treat any non-empty string (incl. "false") as
    // true, since query params always arrive as strings — an explicit enum avoids
    // that footgun and rejects malformed values instead of silently coercing them.
    deleted: z
        .enum(['true', 'false'])
        .optional()
        .transform((v) => (v === undefined ? undefined : v === 'true')),
});
/**
 * Body for the version-projection restore endpoint. `versionSeq` is the
 * stable, monotonic-per-expediente key returned by
 * {@link ExpedienteVersionResponse} — the handler re-applies that version's
 * full case-aggregate snapshot as a NEW audited edit
 * (expediente.version_restored), never mutating the version trail itself
 * (history only grows).
 */
export const restoreExpedienteVersionSchema = z.object({
    versionSeq: z.number().int().positive(),
});
// ---------------------------------------------------------------------------
// Risk matrix — configurable factor weights + thresholds (per tenant).
// The four AML/CFT risk factors (cliente, jurisdicción, canal, producto) carry
// tenant-configurable weights that must sum to 100; the weighted composite
// (0–100) is bucketed into bajo/medio/alto by the two thresholds. The band logic
// per factor is encoded in the scoring engine; only the weights/thresholds are
// configurable here (the meeting's "preliminary weights").
// ---------------------------------------------------------------------------
export const RISK_FACTOR_KEYS = ['cliente', 'jurisdiccion', 'canal', 'producto'];
/** A configurable weight or sub-weight — an integer percentage 0–100. */
const pct100 = z.number().int().min(0).max(100);
export const riskMatrixWeightsSchema = z.object({
    cliente: pct100,
    jurisdiccion: pct100,
    canal: pct100,
    producto: pct100,
});
/**
 * Relative sub-factor weights *within* each risk family. Optional and additive:
 * when a family's sub-weights are present they must sum to 100 and drive the
 * nested calibration/simulator; when absent the family is scored as a single band
 * (the shipped behavior). `producto` is a single-variable factor in the norm
 * (Acuerdo 1-2026 Art. 13.3), so it has no sub-factors.
 */
export const riskSubweightsSchema = z.object({
    cliente: z.object({ profesion: pct100, estructura: pct100, antecedentes: pct100 }).optional(),
    jurisdiccion: z
        .object({ nacionalidad: pct100, origen_destino: pct100, domicilio: pct100, listas: pct100 })
        .optional(),
    canal: z
        .object({ canal_via: pct100, frecuencia: pct100, monto: pct100, instrumento: pct100 })
        .optional(),
});
const subSums100 = (o) => o === undefined || Object.values(o).reduce((a, b) => a + b, 0) === 100;
/**
 * Plantilla de indicadores del tenant, versionada JUNTO con la matriz (mismo blob
 * `factors`, mismo guardado append-only, mismo evento de auditoría):
 * - `bandOverrides`: catKey (`family.subfactor` | 'producto') → entryKey → banda
 *   re-calificada (p. ej. Panamá bajo→alto). Solo se guardan los que difieren del
 *   catálogo base recomendado (que nunca se toca).
 * - `disabledIndicators`: modo (EntityMode) → catKey → entryKeys apagados
 *   ("No usar"); un sub-factor con TODO apagado sale del puntaje y
 *   `effectiveMatrixForMode` re-normaliza los pesos.
 */
export const riskMatrixTemplateSchema = z.object({
    bandOverrides: z
        .record(z.string().max(120), z.record(z.string().max(120), z.enum(['bajo', 'medio', 'alto'])))
        .optional(),
    disabledIndicators: z
        .record(z.string().max(40), z.record(z.string().max(120), z.array(z.string().max(120)).max(500)))
        .optional(),
});
export const riskMatrixSchema = z
    .object({
    weights: riskMatrixWeightsSchema,
    thresholds: z.object({
        medio: z.number().int().min(1).max(99),
        alto: z.number().int().min(2).max(100),
    }),
    subweights: riskSubweightsSchema.optional(),
    /** Plantilla de indicadores (bandas + Usar/No usar) — viaja y versiona con la matriz. */
    template: riskMatrixTemplateSchema.optional(),
})
    .refine((m) => m.weights.cliente + m.weights.jurisdiccion + m.weights.canal + m.weights.producto === 100, { message: 'Las ponderaciones deben sumar 100', path: ['weights'] })
    .refine((m) => m.thresholds.alto > m.thresholds.medio, {
    message: 'El umbral alto debe ser mayor que el medio',
    path: ['thresholds'],
})
    .refine((m) => subSums100(m.subweights?.cliente), {
    message: 'Los sub-pesos de Cliente deben sumar 100',
    path: ['subweights', 'cliente'],
})
    .refine((m) => subSums100(m.subweights?.jurisdiccion), {
    message: 'Los sub-pesos de Jurisdicción deben sumar 100',
    path: ['subweights', 'jurisdiccion'],
})
    .refine((m) => subSums100(m.subweights?.canal), {
    message: 'Los sub-pesos de Canal deben sumar 100',
    path: ['subweights', 'canal'],
});
/**
 * Preliminary flat weights from the compliance meeting. Kept as the engine's
 * fallback when a tenant has saved no matrix (version 0) — do not change without
 * updating the risk-scoring / risk-matrix tests that pin it.
 */
export const DEFAULT_RISK_MATRIX = {
    weights: { cliente: 50, jurisdiccion: 10, canal: 10, producto: 30 },
    thresholds: { medio: 30, alto: 60 },
};
/**
 * Documented, legally-grounded calibration — the one-click "recomendado" preset.
 * Top-level weights track Wolfsberg App. I ranges reconciled to the four Acuerdo
 * 1-2026 Art. 13 factors (Cliente 35 · Geografía/Jurisdicción 25 · Producto 20 ·
 * Transaccionalidad/Canal 20); sub-weights and the 40/70 thresholds come from
 * docs/MATRIZ_RIESGO_FUNDAMENTO.md. Weights are calibration, never law — every
 * tenant may re-calibrate, but each level must keep summing to 100 (SSNF matrix guide).
 */
export const RECOMMENDED_RISK_MATRIX = {
    weights: { cliente: 35, jurisdiccion: 25, canal: 20, producto: 20 },
    thresholds: { medio: 40, alto: 70 },
    subweights: {
        cliente: { profesion: 50, estructura: 20, antecedentes: 30 },
        // `listas` weighs FATF-gris / UE-anexos / Basel membership (Acuerdo 1-2026 Art. 20
        // in fine num. 3 and Art. 21 — non-cooperative-jurisdiction capital/partners; FATF
        // R.19); the FATF black list and country sanctions stay an OVERRIDE, never a weight.
        jurisdiccion: { nacionalidad: 30, origen_destino: 30, domicilio: 15, listas: 25 },
        canal: { canal_via: 40, frecuencia: 30, monto: 20, instrumento: 10 },
    },
};
export const SECTOR_RISK_PRESETS = [
    {
        key: 'general',
        label: 'General (línea base)',
        grupo: 'Financieros',
        mode: 'financiero',
        supervisor: '—',
        fundamento: 'Línea base Wolfsberg App. I conciliada a los 4 factores del Acuerdo 1-2026 Art. 13.',
        weights: { cliente: 35, jurisdiccion: 25, producto: 20, canal: 20 },
    },
    {
        key: 'banca',
        label: 'Banca y grupos bancarios',
        grupo: 'Financieros',
        mode: 'financiero',
        supervisor: 'SBP · Acuerdo 1-2026',
        fundamento: 'Espectro completo: corresponsalía, banca privada y comercio exterior suben Geografía y Producto.',
        weights: { cliente: 30, jurisdiccion: 28, producto: 22, canal: 20 },
    },
    {
        key: 'fiduciarias',
        label: 'Empresas fiduciarias',
        grupo: 'Financieros',
        mode: 'financiero',
        supervisor: 'SBP · Acuerdo 1-2026',
        fundamento: 'Cliente/estructura domina: fideicomisos, estructuras opacas y beneficiario final.',
        weights: { cliente: 40, jurisdiccion: 28, producto: 20, canal: 12 },
    },
    {
        key: 'valores',
        label: 'Mercado de valores — general (SMV)',
        grupo: 'Mercado de valores (SMV)',
        mode: 'valores',
        supervisor: 'SMV · Acuerdo 6-2015',
        fundamento: 'Línea base SMV: cuentas ómnibus, intermediarios y valores al portador. Use un subtipo específico cuando aplique.',
        weights: { cliente: 30, jurisdiccion: 25, producto: 30, canal: 15 },
    },
    // --- Subtipos SMV (sujetos obligados de Ley 23 Art. 22.2 + Acuerdo 6-2015
    // Art. 3), cada uno su propio piso desde su marco de riesgo. Presets paralelos
    // (patrón abogados); el `valores` de arriba queda como entrada general del grupo.
    {
        key: 'valores_casas',
        label: 'Casas de valores',
        grupo: 'Mercado de valores (SMV)',
        mode: 'valores',
        supervisor: 'SMV · Acuerdo 6-2015 Art. 3.2',
        fundamento: 'Custodia de valores/fondos y corresponsalías con custodios extranjeros (Arts. 5.6, 13, 16.5-6) suben Geografía; el producto sigue alto por ómnibus e intermediación.',
        weights: { cliente: 25, jurisdiccion: 30, producto: 30, canal: 15 },
    },
    {
        key: 'valores_admin_inversion',
        label: 'Administradores de inversión',
        grupo: 'Mercado de valores (SMV)',
        mode: 'valores',
        supervisor: 'SMV · Acuerdo 6-2015 Art. 3.3',
        fundamento: 'Cliente/origen de fondos domina: manejo discrecional de activos de terceros con perfil del inversionista y análisis patrimonial (Art. 11) + monitoreo pleno (Art. 23).',
        weights: { cliente: 40, jurisdiccion: 25, producto: 25, canal: 10 },
    },
    {
        key: 'valores_pensiones_cesantia',
        label: 'Administradoras de fondos de pensiones y cesantía',
        grupo: 'Mercado de valores (SMV)',
        mode: 'valores',
        supervisor: 'SMV · Acuerdo 6-2015 Arts. 3.4-3.5',
        fundamento: 'Cliente domina en un perfil que la norma reconoce como bajo: DD simplificada (Art. 37), producto previsional de largo plazo, aportes por planilla y rescate restringido.',
        weights: { cliente: 45, jurisdiccion: 15, producto: 25, canal: 15 },
    },
    {
        key: 'valores_soc_autoadministrada',
        label: 'Sociedades de inversión autoadministradas',
        grupo: 'Mercado de valores (SMV)',
        mode: 'valores',
        supervisor: 'SMV · Acuerdo 6-2015 Art. 3.7',
        fundamento: 'Cliente/estructura domina: al no delegar en un administrador con licencia asumen toda la DD e identificación de inversionista/beneficiario final (examen pleno del Art. 23).',
        weights: { cliente: 45, jurisdiccion: 25, producto: 20, canal: 10 },
    },
    {
        key: 'valores_asesores',
        label: 'Asesores de inversión',
        grupo: 'Mercado de valores (SMV)',
        mode: 'valores',
        supervisor: 'SMV · Acuerdo 6-2015 Art. 3.8',
        fundamento: 'Exposición reducida (estándar tecnológico diferenciado del Art. 23 in fine): sin custodia ni ejecución, el riesgo vive en el cliente introducido y el canal de referimiento.',
        weights: { cliente: 40, jurisdiccion: 20, producto: 15, canal: 25 },
    },
    {
        key: 'valores_psa',
        label: 'Proveedores de servicios administrativos del mercado de valores',
        grupo: 'Mercado de valores (SMV)',
        mode: 'valores',
        supervisor: 'SMV · Acuerdo 6-2015 Art. 3.9',
        fundamento: 'Cliente/estructura domina: back-office sin custodia de efectivo pero con el registro integral de partícipes y beneficiarios finales (examen pleno del Art. 23).',
        weights: { cliente: 45, jurisdiccion: 25, producto: 15, canal: 15 },
    },
    {
        key: 'valores_oar',
        label: 'Organizaciones autorreguladas (bolsas y centrales de valores)',
        grupo: 'Mercado de valores (SMV)',
        mode: 'valores',
        supervisor: 'SMV · Acuerdo 6-2015 Art. 37-A',
        fundamento: 'Riesgo indirecto/estructural: sin clientes minoristas, su exposición es la integridad de los miembros (cliente) y la liquidación de operaciones (producto); geografía y canal mínimos.',
        weights: { cliente: 40, jurisdiccion: 15, producto: 30, canal: 15 },
    },
    {
        key: 'seguros',
        label: 'Seguros — general (SSRP)',
        grupo: 'Seguros (SSRP)',
        mode: 'seguros',
        supervisor: 'SSRP · Ley 23 Art. 22.3',
        fundamento: 'Línea base SSRP: producto domina por vida con valor de rescate, prima única y cancelación temprana. Use un subtipo específico cuando aplique.',
        weights: { cliente: 25, jurisdiccion: 20, producto: 40, canal: 15 },
    },
    // --- Subtipos Seguros (SSRP), cada uno un sujeto obligado distinto de Ley 23
    // Art. 22.3 con su propio piso de riesgo. La aseguradora RETIENE riesgo (producto
    // domina); el corredor/intermediario MEDIA y no lo retiene (Ley 12/2012), así que
    // su exposición es el CANAL, no el producto. Presets paralelos (patrón valores/
    // abogados); el `seguros` de arriba queda como entrada general del grupo. Fundamento
    // por subtipo: docs/indicadores-por-tipo.md §2.4.
    {
        key: 'seguros_vida_ahorro',
        label: 'Aseguradora — vida con ahorro / rescate / anualidades',
        grupo: 'Seguros (SSRP)',
        mode: 'seguros',
        supervisor: 'SSRP · Acuerdo 07-2025 Art. 15/17',
        fundamento: 'Producto domina al máximo: vida individual con ahorro/valor de rescate, prima única y rentas vitalicias son el vehículo de lavado del ramo (Ley 12/2012 Art. 47.1; Acuerdo SSRP 07-2025 Art. 15 a/c excl. DDS + Art. 17 c EDD forzada).',
        weights: { cliente: 30, jurisdiccion: 15, producto: 45, canal: 10 },
    },
    {
        key: 'seguros_generales_fianzas',
        label: 'Aseguradora — ramos generales y fianzas',
        grupo: 'Seguros (SSRP)',
        mode: 'seguros',
        supervisor: 'SSRP · Acuerdo 07-2025 Art. 15',
        fundamento: 'Sin ahorro ni valor de rescate el producto pesa poco (ramos generales DDS-elegibles Art. 15; fianzas Art. 15 b sin EDD forzada): el riesgo residual vive en el cliente/contraparte y su geografía (Ley 12/2012 Art. 47.2-47.3).',
        weights: { cliente: 35, jurisdiccion: 25, producto: 20, canal: 20 },
    },
    {
        key: 'seguros_reaseguradora',
        label: 'Reaseguradora',
        grupo: 'Seguros (SSRP)',
        mode: 'seguros',
        supervisor: 'SSRP · Acuerdo 07-2025 Art. 3.5',
        fundamento: 'Riesgo estructural y transfronterizo: cesión/aceptación entre entidades reguladas con DD limitada por reliance (Acuerdo SSRP 07-2025 Art. 3.5); la geografía del reasegurador y la retrocesión dominan, no el producto retail.',
        weights: { cliente: 35, jurisdiccion: 40, producto: 10, canal: 15 },
    },
    {
        key: 'seguros_corredor',
        label: 'Corredor / intermediario de seguros',
        grupo: 'Seguros (SSRP)',
        mode: 'seguros',
        supervisor: 'SSRP · Ley 12/2012 · Acuerdo 07-2025',
        fundamento: 'Sujeto obligado DISTINTO que MEDIA y no retiene riesgo (Ley 12/2012; corredor/agente): su exposición es el canal — onboarding, cobro de prima y persona interpuesta (Acuerdo SSRP 07-2025, corredor/agente como vía estándar con reliance verificado).',
        weights: { cliente: 30, jurisdiccion: 15, producto: 15, canal: 40 },
    },
    {
        key: 'cooperativas',
        label: 'Cooperativas de ahorro y crédito',
        grupo: 'Financieros',
        mode: 'financiero',
        supervisor: 'IPACOOP',
        fundamento: 'Transaccional/efectivo + cliente (membresía); geografía local.',
        weights: { cliente: 35, jurisdiccion: 20, producto: 15, canal: 30 },
    },
    {
        key: 'financieras',
        label: 'Financieras / leasing / dinero electrónico',
        grupo: 'Financieros',
        mode: 'financiero',
        supervisor: 'SBP',
        fundamento: 'Producto + canal: prepago/e-money, onboarding no presencial y fondeo por terceros.',
        weights: { cliente: 28, jurisdiccion: 20, producto: 27, canal: 25 },
    },
    {
        key: 'remesadoras',
        label: 'Remesadoras (money transfer)',
        grupo: 'Financieros',
        mode: 'financiero',
        supervisor: 'SBP',
        fundamento: 'Geografía + transaccional: corredores país, fraccionamiento y velocidad.',
        weights: { cliente: 20, jurisdiccion: 35, producto: 10, canal: 35 },
    },
    {
        key: 'casas_cambio',
        label: 'Casas de cambio',
        grupo: 'Financieros',
        mode: 'financiero',
        supervisor: 'SBP',
        fundamento: 'Transaccional/EFECTIVO domina: efectivo, divisas y fronteras.',
        weights: { cliente: 20, jurisdiccion: 25, producto: 10, canal: 45 },
    },
    {
        key: 'abogados',
        label: 'Abogados, notarios y contadores (gatekeepers)',
        grupo: 'Abogados y gatekeepers',
        mode: 'abogados',
        supervisor: 'SSNF · Ley 124 Art. 40.11-13',
        fundamento: 'Cliente/estructura domina: creación de sociedades, agente residente y BO oculto.',
        weights: { cliente: 45, jurisdiccion: 25, producto: 20, canal: 10 },
    },
    {
        key: 'abogados_agente',
        label: 'Abogados — agente residente / constitución de sociedades',
        grupo: 'Abogados y gatekeepers',
        mode: 'abogados',
        supervisor: 'SSNF · Ley 124 Art. 40.11',
        fundamento: 'Máxima exposición gatekeeper: sociedades, agente residente y BO oculto; producto y canal mínimos.',
        weights: { cliente: 50, jurisdiccion: 28, producto: 12, canal: 10 },
    },
    {
        key: 'abogados_litigio',
        label: 'Abogados — litigio / asesoría pura',
        grupo: 'Abogados y gatekeepers',
        mode: 'abogados',
        supervisor: 'SSNF (fuera del Art. 40.11 en litigio puro)',
        fundamento: 'El litigio puro queda fuera de las actividades del Art. 40.11: exposición reducida, línea base.',
        weights: { cliente: 35, jurisdiccion: 25, producto: 20, canal: 20 },
    },
    {
        key: 'notarios',
        label: 'Notarías',
        grupo: 'Abogados y gatekeepers',
        mode: 'abogados',
        supervisor: 'SSNF · Ley 124 Art. 40.13',
        fundamento: 'Escrituras de constitución y transferencias inmobiliarias suben Producto; cliente/estructura sigue dominando.',
        weights: { cliente: 40, jurisdiccion: 20, producto: 25, canal: 15 },
    },
    {
        key: 'contadores',
        label: 'Contadores — manejo de fondos / sociedades',
        grupo: 'Abogados y gatekeepers',
        mode: 'abogados',
        supervisor: 'SSNF · Ley 124 Art. 40.12',
        fundamento: 'Manejo de fondos de terceros y creación de sociedades: cliente domina y el canal transaccional pesa.',
        weights: { cliente: 45, jurisdiccion: 22, producto: 13, canal: 20 },
    },
    {
        key: 'inmobiliaria',
        label: 'Inmobiliaria — compraventa / promoción / corretaje',
        grupo: 'Inmobiliaria (SNF)',
        mode: 'inmobiliaria',
        supervisor: 'SSNF · Ley 124 Art. 40.3',
        fundamento: 'Geografía + origen de fondos + monto: efectivo en compraventa y sub/sobrevaluación.',
        weights: { cliente: 30, jurisdiccion: 30, producto: 15, canal: 25 },
    },
    {
        key: 'construccion',
        label: 'Construcción / contratistas',
        grupo: 'No financieros (APNFD)',
        mode: 'financiero',
        hidden: true,
        supervisor: 'SSNF · Ley 124 Art. 40.4',
        fundamento: 'Cliente (contraparte/contratos públicos) + transaccional (montos y sobrecostos).',
        weights: { cliente: 35, jurisdiccion: 20, producto: 15, canal: 30 },
    },
    {
        key: 'casinos',
        label: 'Casinos y juegos de azar',
        grupo: 'No financieros (APNFD)',
        mode: 'financiero',
        hidden: true,
        supervisor: 'SSNF · Ley 124 Art. 40.2',
        fundamento: 'Transaccional/EFECTIVO + canal online: fichas, structuring y terceros.',
        weights: { cliente: 20, jurisdiccion: 15, producto: 20, canal: 45 },
    },
    {
        key: 'metales',
        label: 'Metales y piedras preciosas / joyería',
        grupo: 'No financieros (APNFD)',
        mode: 'financiero',
        hidden: true,
        supervisor: 'SSNF · Ley 124 Art. 40.7',
        fundamento: 'Efectivo + geografía (comercio transfronterizo sobre umbral).',
        weights: { cliente: 20, jurisdiccion: 30, producto: 15, canal: 35 },
    },
    {
        key: 'zona_libre',
        label: 'Zona Libre de Colón / zonas francas',
        grupo: 'No financieros (APNFD)',
        mode: 'financiero',
        hidden: true,
        supervisor: 'SSNF · Ley 124 Art. 40.1',
        fundamento: 'Geografía (TBML) domina: sub/sobre-facturación y terceros países.',
        weights: { cliente: 20, jurisdiccion: 40, producto: 15, canal: 25 },
    },
    {
        key: 'autos',
        label: 'Compraventa de autos',
        grupo: 'No financieros (APNFD)',
        mode: 'financiero',
        hidden: true,
        supervisor: 'SSNF · Ley 124 Art. 40.10',
        fundamento: 'Efectivo + cliente + monto; terceros y sub/sobrevaluación.',
        weights: { cliente: 25, jurisdiccion: 15, producto: 20, canal: 40 },
    },
    {
        key: 'empeno',
        label: 'Casas de empeño',
        grupo: 'No financieros (APNFD)',
        mode: 'financiero',
        hidden: true,
        supervisor: 'SSNF · Ley 124 Art. 40.6',
        fundamento: 'Efectivo + producto (bienes de procedencia dudosa).',
        weights: { cliente: 25, jurisdiccion: 15, producto: 25, canal: 35 },
    },
    {
        key: 'transporte_valores',
        label: 'Transporte de valores',
        grupo: 'No financieros (APNFD)',
        mode: 'financiero',
        hidden: true,
        supervisor: 'SSNF · Ley 124 Art. 40.5',
        fundamento: 'Transaccional/efectivo + contraparte.',
        weights: { cliente: 25, jurisdiccion: 20, producto: 15, canal: 40 },
    },
    {
        key: 'vasp',
        label: 'VASP / criptoactivos',
        grupo: 'Emergente',
        mode: 'financiero',
        hidden: true,
        supervisor: 'GAFI R.15 (sin régimen vigente en PA)',
        fundamento: 'Producto + canal: anonimato, cross-border y Travel Rule. Buena práctica GAFI, no obligación legal aún.',
        weights: { cliente: 20, jurisdiccion: 25, producto: 30, canal: 25 },
    },
];
/** Full matrix config for a sector preset: its weight tilt + the general recommended sub-weights/thresholds. */
export function sectorRiskMatrix(preset) {
    return {
        weights: preset.weights,
        thresholds: RECOMMENDED_RISK_MATRIX.thresholds,
        subweights: RECOMMENDED_RISK_MATRIX.subweights,
    };
}
/**
 * The active (non-"Otros") sector presets a firm in a given mode may pick from.
 * Filters `SECTOR_RISK_PRESETS` by `mode` and drops any marked `hidden` (the
 * APNFD/emergent groups withheld "por ahora"). Preserves preset order so the UI
 * grouping stays stable. `SECTOR_RISK_PRESETS` itself is untouched, so the data
 * is still available to re-enable the hidden sectors later.
 */
export function sectorPresetsForMode(mode) {
    return SECTOR_RISK_PRESETS.filter((p) => p.mode === mode && !p.hidden);
}
// ---------------------------------------------------------------------------
// AI assistant — a closed set of scoped, data-backed actions (not open chat).
// Each skill maps to a specific action over the tenant's own data; the model
// only phrases the answer, and there's always a deterministic fallback.
// ---------------------------------------------------------------------------
export const assistantSkillSchema = z.enum(['explain_risk']);
export const assistantAskSchema = z.object({
    skill: assistantSkillSchema,
    expedienteId: z.string().uuid(),
});
/** Free-text assistant chat. Tight caps are part of the abuse controls. */
export const assistantChatTurnSchema = z.object({
    role: z.enum(['user', 'assistant']),
    text: z.string().max(4000),
});
export const assistantChatSchema = z.object({
    message: z.string().trim().min(1).max(2000),
    history: z.array(assistantChatTurnSchema).max(8).optional(),
    /** Optional current-client context so "este cliente" resolves without an id. */
    expedienteId: z.string().uuid().optional(),
    /** Opaque client correlation id that groups a conversation's turns in the AI
     * decision log. Tenant scoping still comes ONLY from the JWT (I2) — the record
     * is written under the caller's tenant regardless of this value. Generated
     * server-side when absent and echoed back so the client can link later turns. */
    conversationId: z.string().uuid().optional(),
});
/** Copilot execute envelope (propose→confirm→execute). Kept permissive: the
 * tool registry validates the per-tool `input` shape server-side once `tool` is
 * resolved, so this only guards the envelope (a known tool name + an object). */
export const assistantExecuteSchema = z.object({
    tool: z.string().min(1).max(64),
    input: z.record(z.unknown()),
    /** Conversation this confirmation belongs to (from the chat response echo) —
     * links the executed decision to its thread in the AI decision log. */
    conversationId: z.string().uuid().optional(),
    /** The chat turn whose proposal is being confirmed — ties the decision back to
     * the exact suggestion + its evidence. */
    turnId: z.string().uuid().optional(),
    /** Anti-rubber-stamp signal (B3): true when the user edited the proposal before
     * confirming. Persisted as `editedFromProposal` — governance evidence that a
     * human exercised judgement rather than blindly accepting the AI's suggestion. */
    edited: z.boolean().optional(),
});
// ---------------------------------------------------------------------------
// Expediente document schemas.
// ---------------------------------------------------------------------------
export const expedienteDocEstadoSchema = z.enum([
    'Vigente',
    'Por Vencer',
    'Vencido',
    'Bajo Revisión',
    'Aceptado',
    'Recibido',
    'Solicitado',
]);
export const createExpedienteDocumentSchema = z.object({
    category: z.string().trim().min(1).max(120),
    name: z.string().trim().min(1).max(254),
    expiryDate: isoDateSchema.optional(),
    documentTypeId: z.string().uuid().optional(),
    // Fase 1 drill-down — attach this doc to a specific related person; absent =
    // entity-level document. The handler verifies the owner is in the same
    // tenant+expediente (I1/T1) before persisting.
    ownerId: z.string().uuid().optional(),
});
const updateExpedienteDocumentFields = z.object({
    estado: expedienteDocEstadoSchema.optional(),
    expiryDate: isoDateSchema.nullable().optional(),
    name: z.string().trim().min(1).max(254).optional(),
});
export const updateExpedienteDocumentSchema = updateExpedienteDocumentFields.refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'Al menos un campo es requerido' });
// File upload — presigned S3 PUT then blob registration. The MIME allowlist is
// what the OCR engine can read (PDF + common image formats); enforced at both
// presign and register, and surfaced to the file picker via `.options`.
const documentMimeSchema = z.enum([
    'application/pdf',
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/tiff',
]);
export const ALLOWED_DOCUMENT_MIME_TYPES = documentMimeSchema.options;
export const MAX_DOCUMENT_UPLOAD_BYTES = 15 * 1024 * 1024; // 15 MB
const uploadFileNameSchema = z.string().trim().min(1).max(254);
export const documentUploadUrlSchema = z.object({
    fileName: uploadFileNameSchema,
    contentType: documentMimeSchema,
});
export const registerDocumentBlobSchema = z.object({
    s3Key: z.string().trim().min(1).max(1024),
    fileName: uploadFileNameSchema,
    contentType: documentMimeSchema,
    sizeBytes: z.number().int().positive().max(MAX_DOCUMENT_UPLOAD_BYTES).optional(),
});
export const listExpedienteDocumentsQuerySchema = z.object({
    // Soft-delete (papelera) filter: true lists the trashed documents instead of the
    // active ones. Same enum-not-coerce guard as listExpedientesQuerySchema — query
    // params arrive as strings, so z.coerce.boolean() would read "false" as true.
    deleted: z
        .enum(['true', 'false'])
        .optional()
        .transform((v) => (v === undefined ? undefined : v === 'true')),
    // Fase 4 drill-down (req#7): filter to a single related person's documents
    // (owner_id = X). Absent = every document of the expediente (entity + persons).
    // Tenant/expediente-scoped in the db layer, so a foreign uuid yields an empty
    // list, never a leak (I1/T1).
    ownerId: z.string().uuid().optional(),
});
// Batch sanctions screening — upload a list of names, screen each against the
// watchlists (OFAC/UN), get a coincidence report.
export const screeningBatchItemSchema = z.object({
    name: z.string().trim().min(1).max(254),
    country: z.string().trim().max(80).optional(),
});
export const screeningBatchSchema = z.object({
    items: z.array(screeningBatchItemSchema).min(1).max(200),
});
// ---------------------------------------------------------------------------
// §4 screening recurrente — re-screen the base against the lists + list refresh.
// ---------------------------------------------------------------------------
/**
 * Which slice of the base a rescreen run covers:
 *  - `all`  — every subject (the "cada 6 meses" full re-verification pass).
 *  - `due`  — only subjects whose screening_schedules.next_run_at <= now (the
 *             cadence-driven re-screen the EventBridge scheduler drives, §4-infra).
 *  - `bajo` — only low-risk subjects: the CONTINUOUS pseudonymised pipeline that
 *             watches whether a `bajo` is really an `alto`. Its persisted trace is
 *             keyed by pseudonymKey (no raw PII in the pipeline log).
 */
export const rescreenModeSchema = z.enum(['all', 'due', 'bajo']);
/**
 * Query for the rescreen endpoint. `mode` defaults to `all` (full re-verification).
 * The sweep is BOUNDED and PAGEABLE so it can't time out a Lambda on a large tenant:
 *  - `limit`   how many subjects to process this page (default 200, max 1000).
 *  - `afterId` opaque cursor from a prior page's `nextCursor` — resume after it.
 * The response returns `hasMore` + `nextCursor`; the §4-infra scheduler pages the
 * whole base by following the cursor (no silent truncation). All three modes are
 * bounded the same way.
 */
export const rescreenQuerySchema = z.object({
    mode: rescreenModeSchema.default('all'),
    limit: z.coerce.number().int().min(1).max(1000).default(200),
    afterId: z.string().trim().min(1).max(200).optional(),
});
// The lists-refresh endpoint takes no body — the provider re-pulls its configured
// sources. Kept as a no-op schema so the handler validates like every other write.
// ---------------------------------------------------------------------------
// §6 adverse-media — news scraper tied to the bitácora (a SEPARATE EDD signal).
// ---------------------------------------------------------------------------
/**
 * Body for POST /api/v1/screening/adverse-media — run the news watch for ONE
 * subject. The provider searches Google/GDELT for the REAL name (a hash can't be
 * searched); the persisted trace for a `bajo`-risk subject is pseudonymised (§4).
 *  - `name`         the subject's real name to search (required — the query needs it).
 *  - `subjectId`    the subject's business key (cédula/RUC or owner subject_id).
 *  - `subjectType`  natural | legal (defaults to natural).
 *  - `expedienteId` the case to tie the bitácora entry + alert to (optional: an
 *                   ad-hoc name-only check records nothing to the bitácora).
 *  - `country`      optional country hint to narrow the news search.
 *  - `mode`         `bajo` pseudonymises the persisted trace/subject_id (no raw PII
 *                   in the pipeline log); `standard` (default) keys by subject_id.
 */
export const adverseMediaCheckSchema = z.object({
    name: z.string().trim().min(1).max(254),
    subjectId: z.string().trim().min(1).max(80),
    // Inlined (not subjectTypeSchema) — that const is declared later in this file.
    subjectType: z.enum(['natural', 'legal']).default('natural'),
    expedienteId: z.string().uuid().optional(),
    country: z.string().trim().max(80).optional(),
    mode: z.enum(['standard', 'bajo']).default('standard'),
});
// ---------------------------------------------------------------------------
// Subject-profile schemas (subject of an expediente).
// ---------------------------------------------------------------------------
// SubjectType ('natural' | 'legal') is already exported from validations.ts.
export const subjectTypeSchema = z.enum(['natural', 'legal']);
export const createSubjectSchema = z.object({
    // Business key (cédula/RUC); named subjectRef to avoid colliding with the
    // {subjectId} path param. Maps to the subject_id column.
    subjectRef: z.string().trim().min(1).max(50),
    subjectType: subjectTypeSchema,
    declaredAddress: z.string().trim().max(500).optional(),
    sector: z.string().trim().max(120).optional(),
    activity: z.string().trim().max(254).optional(),
    requiredDocuments: z.array(z.string().trim().min(1).max(254)).optional(),
});
const updateSubjectFields = z.object({
    subjectType: subjectTypeSchema.optional(),
    declaredAddress: z.string().trim().max(500).nullable().optional(),
    sector: z.string().trim().max(120).nullable().optional(),
    activity: z.string().trim().max(254).nullable().optional(),
    requiredDocuments: z.array(z.string().trim().min(1).max(254)).optional(),
});
export const updateSubjectSchema = updateSubjectFields.refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'Al menos un campo es requerido' });
// ---------------------------------------------------------------------------
// Subject-owner schemas (ownership/control graph — UBO — of an expediente).
// ---------------------------------------------------------------------------
export const ownerTypeSchema = z.enum(['natural', 'legal']);
/**
 * Rol de la persona relacionada (Fase 1). Sociedad: director/dignatario/accionista/
 * beneficiario_final. Fundación: fundador/consejo_fundacional/beneficiario_final.
 */
export const ownerRolSchema = z.enum([
    'director',
    'dignatario',
    'accionista',
    'beneficiario_final',
    'fundador',
    'consejo_fundacional',
]);
/** Posición patrimonial — solo Fundación (criterio por posición, no por umbral %). */
export const ownerPosicionSchema = z.enum(['primario', 'secundario']);
/**
 * Per-person KYC block persisted as `subject_owners.kyc` (jsonb). Bounded set of
 * the template's DDC fields (Ley 23 Art. 27); all optional. The fields the matrix /
 * Declaración Jurada consume (rol, posicion, pais_residencia_fiscal,
 * porcentaje_patrimonial, pep_*) are typed columns, not here.
 */
export const ownerKycSchema = z.object({
    fechaNacimiento: isoDateSchema.optional(),
    paisNacimiento: z.string().trim().max(120).optional(),
    tipoDocumento: z.string().trim().max(60).optional(),
    numeroDocumento: z.string().trim().max(60).optional(),
    nacionalidad: z.string().trim().max(120).optional(),
    fechaEmision: isoDateSchema.optional(),
    fechaExpiracion: isoDateSchema.optional(),
    direccionResidencial: z.string().trim().max(500).optional(),
    direccionPostal: z.string().trim().max(500).optional(),
    telefono: z.string().trim().max(30).optional(),
    celular: z.string().trim().max(30).optional(),
    correo: z.string().trim().max(254).optional(),
    ocupacion: z.string().trim().max(254).optional(),
    fechaInicioBf: isoDateSchema.optional(),
    fechaFinBf: isoDateSchema.optional(),
});
export const createOwnerSchema = z.object({
    // The subject this ownership pertains to (cédula/RUC); maps to subject_id.
    subjectRef: z.string().trim().min(1).max(50),
    name: z.string().trim().min(1).max(254),
    ownerType: ownerTypeSchema,
    ownershipPct: z.number().min(0).max(100),
    parentOwnerId: z.string().uuid().nullable().optional(),
    idNumber: z.string().trim().max(50).optional(),
    country: z.string().trim().max(120).optional(),
    kycComplete: z.boolean().optional(),
    kycExpedienteId: z.string().uuid().nullable().optional(),
    // Fase 1 — rol, criterio Fundación (posición), y campos KYC por persona.
    rol: ownerRolSchema.optional(),
    posicion: ownerPosicionSchema.optional(),
    paisResidenciaFiscal: iso2CountrySchema.optional(),
    porcentajePatrimonial: z.number().min(0).max(100).optional(),
    pepPropio: z.boolean().optional(),
    pepFamiliar: z.boolean().optional(),
    usPerson: z.boolean().optional(),
    declaraImpuestosUs: z.boolean().optional(),
    multiplesNacionalidades: z.boolean().optional(),
    kyc: ownerKycSchema.optional(),
});
const updateOwnerFields = z.object({
    name: z.string().trim().min(1).max(254).optional(),
    ownerType: ownerTypeSchema.optional(),
    ownershipPct: z.number().min(0).max(100).optional(),
    parentOwnerId: z.string().uuid().nullable().optional(),
    idNumber: z.string().trim().max(50).nullable().optional(),
    country: z.string().trim().max(120).nullable().optional(),
    kycComplete: z.boolean().optional(),
    kycExpedienteId: z.string().uuid().nullable().optional(),
    // Fase 1 — nullable so an edit may clear a captured value.
    rol: ownerRolSchema.nullable().optional(),
    posicion: ownerPosicionSchema.nullable().optional(),
    paisResidenciaFiscal: iso2CountrySchema.nullable().optional(),
    porcentajePatrimonial: z.number().min(0).max(100).nullable().optional(),
    pepPropio: z.boolean().optional(),
    pepFamiliar: z.boolean().optional(),
    usPerson: z.boolean().optional(),
    declaraImpuestosUs: z.boolean().optional(),
    multiplesNacionalidades: z.boolean().optional(),
    kyc: ownerKycSchema.nullable().optional(),
});
export const updateOwnerSchema = updateOwnerFields.refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'Al menos un campo es requerido' });
// ---------------------------------------------------------------------------
// Declaración Jurada de Beneficiario Final — snapshot firmado, inmutable,
// versionado (D3). This schema validates the `declared_snapshot` jsonb: the facts
// as attested at a moment (declarante + each BF + threshold + entity kind). The
// endpoint that mints a version arrives in Fase 5; here only the shape is frozen.
// ---------------------------------------------------------------------------
export const declaracionJuradaSnapshotSchema = z.object({
    declarante: z.object({
        nombre: z.string().trim().min(1).max(254),
        documento: z.string().trim().min(1).max(60),
        cargo: z.string().trim().max(120).optional(),
    }),
    beneficiarios: z.array(z.object({
        nombre: z.string().trim().min(1).max(254),
        documento: z.string().trim().min(1).max(60),
        nacionalidad: z.string().trim().max(120).optional(),
        paisResidenciaFiscal: iso2CountrySchema.optional(),
        porcentajePatrimonial: z.number().min(0).max(100).optional(),
    })),
    umbralAplicado: z.number().min(0).max(100),
    entityType: expedienteEntityTypeSchema,
});
/**
 * Request body to MINT a Declaración Jurada version (D3). Only the declarante and
 * the signature metadata come from the client; the beneficiarios, umbral and entity
 * kind are FROZEN server-side from the current subject_owners + tenant sector (never
 * client-supplied), so the sworn snapshot cannot be forged. `signedAt` is an ISO
 * timestamp; `signatureEvidence` records method/IP/e-sign reference (Ley 51/2008).
 */
export const createDeclaracionJuradaSchema = z.object({
    declarante: declaracionJuradaSnapshotSchema.shape.declarante,
    signedBy: z.string().trim().min(1).max(254).optional(),
    signedAt: z.string().datetime().optional(),
    signatureEvidence: z
        .object({
        metodo: z.string().trim().max(120).optional(),
        ip: z.string().trim().max(60).optional(),
        referencia: z.string().trim().max(254).optional(),
    })
        .optional(),
});
// ---------------------------------------------------------------------------
// Document-request notification schema.
// ---------------------------------------------------------------------------
export const notificationChannelSchema = z.enum(['whatsapp', 'email']);
export const requestDocumentsSchema = z.object({
    channels: z.array(notificationChannelSchema).nonempty(),
    message: z.string().trim().max(1000).optional(),
});
// ---------------------------------------------------------------------------
// Alert (system-generated case) schemas — read + transition only (no create).
// ---------------------------------------------------------------------------
export const alertPrioritySchema = z.enum(['Alta', 'Media', 'Baja']);
/**
 * The `alert_type` opened by the §3 debida-diligencia renewal alarm. `alert_type`
 * is a free string in the DB (like 'Coincidencia PEP' / 'Vigilancia registral');
 * this shared es-PA constant is the single source of truth so the API that opens
 * the alert and the web that renders/filters it never diverge on the label.
 */
export const ALERT_TYPE_RENOVACION = 'Renovación de debida diligencia';
/** Alarm priority mapped from the expediente's risk nivel (más riesgo = más urgente). */
export const RENOVACION_PRIORITY_BY_NIVEL = {
    alto: 'Alta',
    medio: 'Media',
    bajo: 'Baja',
};
/**
 * The `alert_type` opened by the §4 screening re-verification when a re-screen
 * delta RAISES a subject's risk (ok/review → hit, or ok → review). Single source
 * of truth shared by the API (opens the alert), the web (renders/filters it), and
 * the migration's partial-unique dedup index — keep the three in lockstep.
 */
export const ALERT_TYPE_SCREENING_DELTA = 'Coincidencia en re-verificación de listas';
/** Alarm priority for a raising screening delta: a blocking `hit` is urgent, a `review` less so. */
export const SCREENING_DELTA_PRIORITY_BY_STATUS = {
    hit: 'Alta',
    review: 'Media',
};
/**
 * The `alert_type` opened when a re-screen delta LOWERS a subject's screening
 * status (hit/review → ok, or hit → review): the prior match is no longer found on
 * the lists. INFORMATIONAL only — the alert asks a human to confirm the exit; it
 * NEVER auto-lowers the expediente's riesgo/score/estado (auto-downgrading on a
 * vanished hit is dangerous — the list entry may have moved, not been delisted).
 * Single source of truth shared by the API (opens the alert), the web
 * (renders/filters it), and migration 0015's partial-unique dedup index — keep the
 * three in lockstep. Distinct from ALERT_TYPE_SCREENING_DELTA so a raising-delta
 * alert and a cleared alert dedup independently.
 */
export const ALERT_TYPE_SCREENING_CLEARED = 'Posible salida de lista — requiere revisión';
/** A cleared/off-list delta is informational (human adjudication) → always the lowest priority. */
export const SCREENING_CLEARED_ALERT_PRIORITY = 'Baja';
/**
 * The `alert_type` opened by the §6 adverse-media watch when a news search surfaces
 * a RELEVANT adverse hit for a subject. Distinct from ALERT_TYPE_SCREENING_DELTA so
 * an adverse-media alert and a sanctions-delta alert can coexist OPEN on the SAME
 * subject (each keyed independently). Single source of truth shared by the API
 * (opens the alert), the web (renders/filters it), and the migration's partial-
 * unique dedup index — keep the three in lockstep. Adverse media NEVER blocks: the
 * alert is always the EDD priority (Media), a lead for human adjudication.
 */
export const ALERT_TYPE_ADVERSE_MEDIA = 'Coincidencia en medios adversos';
/** Adverse media is a signal, not a sanction → its alert is always the EDD priority. */
export const ADVERSE_MEDIA_ALERT_PRIORITY = 'Media';
/**
 * The `alert_type` opened by the Registro Público "sweep" when a client's RP status
 * changed since the last check (estado / denominación / miembros). Single source of
 * truth shared by the API (opens the alert), the web (renders/filters it), and the
 * migration's partial-unique dedup index — keep the three in lockstep. Keyed on
 * (tenant, expediente) so it dedups independently of the subject-keyed screening
 * alerts. A registral change (e.g. a sociedad disuelta / suspendida or a change of
 * dignatarios) is EDD-relevant → priority 'Alta' for prompt human review.
 */
export const ALERT_TYPE_RP_CHANGE = 'Cambio en Registro Público';
/** An RP status change always warrants prompt review → fixed 'Alta' priority. */
export const RP_CHANGE_ALERT_PRIORITY = 'Alta';
export const listAlertsQuerySchema = z.object({
    estado: z.string().trim().min(1).max(120).optional(),
    expedienteId: z.string().uuid().optional(),
    priority: alertPrioritySchema.optional(),
});
// ---------------------------------------------------------------------------
// Renewal (§3) — due-detection query for the debida-diligencia renewal alarms.
// ---------------------------------------------------------------------------
/**
 * Query for the due-renewals endpoint. `windowDays` (default 0) widens the
 * horizon: 0 = only what is already due (scheduled_for <= today); N = also
 * surface renewals coming due within the next N days ("próxima"). Coerced from
 * the query string; capped so a caller can't ask for an unbounded scan.
 */
export const dueRenewalsQuerySchema = z.object({
    windowDays: z.coerce.number().int().min(0).max(365).default(0),
});
const updateAlertFields = z.object({
    estado: z.string().trim().min(1).max(120).optional(),
    priority: alertPrioritySchema.optional(),
    assignedTo: z.string().uuid().nullable().optional(),
    resolutionCode: z.string().trim().min(1).max(120).nullable().optional(),
    resolutionNote: z.string().trim().max(1000).nullable().optional(),
});
export const updateAlertSchema = updateAlertFields.refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'Al menos un campo es requerido' });
// ---------------------------------------------------------------------------
// Scheduled-review schemas (periodic re-review of an expediente).
// ---------------------------------------------------------------------------
export const reviewTypeSchema = z.enum(['periodica', 'por_riesgo', 'evento']);
export const reviewStatusSchema = z.enum([
    'pendiente',
    'en_proceso',
    'completada',
    'omitida',
]);
export const createReviewSchema = z.object({
    scheduledFor: isoDateSchema,
    reviewType: reviewTypeSchema.default('periodica'),
    riskLevel: z.string().trim().min(1).max(120).optional(),
    cadenceMonths: z.number().int().positive().optional(),
    assignedTo: z.string().uuid().nullable().optional(),
});
const updateReviewFields = z.object({
    scheduledFor: isoDateSchema.optional(),
    reviewType: reviewTypeSchema.optional(),
    riskLevel: z.string().trim().min(1).max(120).nullable().optional(),
    cadenceMonths: z.number().int().positive().nullable().optional(),
    status: reviewStatusSchema.optional(),
    assignedTo: z.string().uuid().nullable().optional(),
});
export const updateReviewSchema = updateReviewFields.refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'Al menos un campo es requerido' });
// ---------------------------------------------------------------------------
// Credit-consent schemas (credit-bureau/APC consent on file for an expediente).
// ---------------------------------------------------------------------------
export const consentSubjectTypeSchema = z.enum(['natural', 'legal']);
/** Full ISO-8601 timestamp (timestamptz). granted_at/expires_at/revoked_at. */
export const isoDateTimeSchema = z.string().datetime({ offset: true });
export const createConsentSchema = z.object({
    // Business key (cédula/RUC); named subjectRef to avoid colliding with the
    // {subjectId} path param. Maps to the subject_id column.
    subjectRef: z.string().trim().min(1).max(50),
    subjectType: consentSubjectTypeSchema,
    // Where the consent was obtained (required).
    source: z.string().trim().min(1).max(254),
    evidenceRef: z.string().trim().max(500).optional(),
    grantedAt: isoDateTimeSchema.optional(),
    expiresAt: isoDateTimeSchema.nullable().optional(),
});
const updateConsentFields = z.object({
    source: z.string().trim().min(1).max(254).optional(),
    evidenceRef: z.string().trim().max(500).nullable().optional(),
    expiresAt: isoDateTimeSchema.nullable().optional(),
    // Revoking = setting revokedAt to a timestamp (or clearing it back to null).
    revokedAt: isoDateTimeSchema.nullable().optional(),
});
export const updateConsentSchema = updateConsentFields.refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'Al menos un campo es requerido' });
//# sourceMappingURL=schemas.js.map